import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SubtaskAssignee {
  member_id: string;
  completed_at: string | null;
  member?: { display_name: string | null } | null;
}

export interface Subtask {
  id: string;
  title: string;
  description: string | null;
  status: "pending" | "completed" | "cancelled";
  assigned_member_id: string;
  due_date: string | null;
  due_at: string | null;
  order_index: number;
  completed_at: string | null;
  assignee?: { display_name: string | null } | null;
  assignees: SubtaskAssignee[];
}

export interface AddSubtaskInput {
  title: string;
  description?: string | null;
  assignee_member_ids: string[];
  due_date?: string | null;
  due_at?: string | null;
}

export interface UpdateSubtaskInput {
  id: string;
  title?: string;
  description?: string | null;
  assignee_member_ids?: string[];
  due_date?: string | null;
  due_at?: string | null;
}

const SUBTASK_SELECT = `id, title, description, status, assigned_member_id, due_date, due_at,
  order_index, completed_at, assignee:assigned_member_id(display_name),
  assignees:workspace_task_assignees(member_id, completed_at, member:member_id(display_name))`;

interface UseTaskSubtasksArgs {
  parentTaskId: string | null;
  workspaceId: string;
  memberId: string;
  enabled?: boolean;
}

async function syncSubtaskAssignees(
  taskId: string,
  workspaceId: string,
  memberIds: string[],
) {
  const uniqueIds = Array.from(new Set(memberIds));
  if (uniqueIds.length === 0) {
    throw new Error("Selecione ao menos um responsável");
  }

  const { data: existing, error: fetchError } = await supabase
    .from("workspace_task_assignees")
    .select("member_id")
    .eq("task_id", taskId);
  if (fetchError) throw fetchError;

  const existingIds = new Set((existing ?? []).map((row) => row.member_id));
  const toRemove = [...existingIds].filter((id) => !uniqueIds.includes(id));
  const toAdd = uniqueIds.filter((id) => !existingIds.has(id));

  if (toRemove.length > 0) {
    const { error } = await supabase
      .from("workspace_task_assignees")
      .delete()
      .eq("task_id", taskId)
      .in("member_id", toRemove);
    if (error) throw error;
  }

  if (toAdd.length > 0) {
    const { error } = await supabase.from("workspace_task_assignees").insert(
      toAdd.map((member_id) => ({
        task_id: taskId,
        member_id,
        workspace_id: workspaceId,
      })),
    );
    if (error) throw error;
  }

  const { error: primaryError } = await supabase
    .from("workspace_tasks")
    .update({ assigned_member_id: uniqueIds[0] })
    .eq("id", taskId);
  if (primaryError) throw primaryError;
}

export function useTaskSubtasks({
  parentTaskId,
  workspaceId,
  memberId,
  enabled = true,
}: UseTaskSubtasksArgs) {
  const [subtasks, setSubtasks] = useState<Subtask[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const listSubtasks = useCallback(async () => {
    if (!parentTaskId || !enabled) {
      setSubtasks([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("workspace_tasks")
        .select(SUBTASK_SELECT)
        .eq("parent_task_id", parentTaskId)
        .order("order_index", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      setSubtasks((data ?? []) as unknown as Subtask[]);
    } catch (err: unknown) {
      toast({
        title: "Erro ao carregar subtarefas",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [parentTaskId, enabled, toast]);

  useEffect(() => {
    void listSubtasks();
  }, [listSubtasks]);

  const addSubtask = useCallback(
    async (input: AddSubtaskInput) => {
      if (!parentTaskId) throw new Error("tarefa pai ausente");
      const title = input.title.trim();
      if (!title) throw new Error("Informe um título");

      const assigneeIds =
        input.assignee_member_ids.length > 0
          ? input.assignee_member_ids
          : [memberId];

      const nextOrder =
        subtasks.reduce((max, s) => Math.max(max, s.order_index ?? 0), -1) + 1;

      const { data, error } = await supabase
        .from("workspace_tasks")
        .insert({
          workspace_id: workspaceId,
          parent_task_id: parentTaskId,
          title,
          description: input.description?.trim() || null,
          assigned_member_id: assigneeIds[0],
          created_by_member_id: memberId,
          due_date: input.due_date ?? null,
          due_at: input.due_date ? (input.due_at ?? null) : null,
          order_index: nextOrder,
          status: "pending",
        })
        .select("id")
        .single();
      if (error) throw error;

      if (data?.id && assigneeIds.length > 1) {
        await syncSubtaskAssignees(data.id, workspaceId, assigneeIds);
      }

      await listSubtasks();
      return data?.id as string | undefined;
    },
    [parentTaskId, workspaceId, memberId, subtasks, listSubtasks],
  );

  const updateSubtask = useCallback(
    async (input: UpdateSubtaskInput) => {
      const patch: Record<string, unknown> = {};
      if (input.title !== undefined) patch.title = input.title.trim();
      if (input.description !== undefined)
        patch.description = input.description?.trim() || null;
      if (input.due_date !== undefined) {
        patch.due_date = input.due_date;
        patch.due_at = input.due_date ? (input.due_at ?? null) : null;
      } else if (input.due_at !== undefined) {
        patch.due_at = input.due_at;
      }

      if (Object.keys(patch).length > 0) {
        const { error } = await supabase
          .from("workspace_tasks")
          .update(patch)
          .eq("id", input.id);
        if (error) throw error;
      }

      if (input.assignee_member_ids !== undefined) {
        await syncSubtaskAssignees(input.id, workspaceId, input.assignee_member_ids);
      }

      await listSubtasks();
    },
    [workspaceId, listSubtasks],
  );

  const toggleAssigneeCompletion = useCallback(
    async (taskId: string, assigneeMemberId: string, completed: boolean) => {
      const { error } = await supabase
        .from("workspace_task_assignees")
        .update({ completed_at: completed ? new Date().toISOString() : null })
        .eq("task_id", taskId)
        .eq("member_id", assigneeMemberId);
      if (error) throw error;
      await listSubtasks();
    },
    [listSubtasks],
  );

  const reopenSubtask = useCallback(
    async (id: string) => {
      const { error } = await supabase
        .from("workspace_task_assignees")
        .update({ completed_at: null })
        .eq("task_id", id);
      if (error) throw error;
      await listSubtasks();
    },
    [listSubtasks],
  );

  const removeSubtask = useCallback(
    async (id: string) => {
      const { error } = await supabase
        .from("workspace_tasks")
        .update({ status: "cancelled", outcome: null, completed_at: null })
        .eq("id", id);
      if (error) throw error;
      await listSubtasks();
    },
    [listSubtasks],
  );

  const reorderSubtasks = useCallback(
    async (orderedIds: string[]) => {
      setSubtasks((prev) => {
        const byId = new Map(prev.map((s) => [s.id, s]));
        return orderedIds
          .map((id, idx) => {
            const s = byId.get(id);
            return s ? { ...s, order_index: idx } : null;
          })
          .filter((s): s is Subtask => s !== null);
      });

      await Promise.all(
        orderedIds.map((id, idx) =>
          supabase.from("workspace_tasks").update({ order_index: idx }).eq("id", id),
        ),
      );
      await listSubtasks();
    },
    [listSubtasks],
  );

  return {
    subtasks,
    loading,
    listSubtasks,
    addSubtask,
    updateSubtask,
    toggleAssigneeCompletion,
    reopenSubtask,
    removeSubtask,
    reorderSubtasks,
  };
}
